import { useState, useEffect, DependencyList } from "react";

export interface AsyncState<T> {
    data: T | null;
    loading: boolean;
    error: string | null;
}

/**
 * Unified async-data hook. Runs `fetcher` whenever `deps` change,
 * returning a stable { data, loading, error } shape.
 * Cancels stale results automatically to prevent state updates after unmount.
 */
export function useAsyncData<T>(
    fetcher: () => Promise<T>,
    deps: DependencyList
): AsyncState<T> {
    const [state, setState] = useState<AsyncState<T>>({
        data: null,
        loading: true,
        error: null,
    });

    useEffect(() => {
        let cancelled = false;
        setState((prev) => ({ ...prev, loading: true, error: null }));

        fetcher()
            .then((data) => {
                if (!cancelled) setState({ data, loading: false, error: null });
            })
            .catch((err: unknown) => {
                if (!cancelled)
                    setState({
                        data: null,
                        loading: false,
                        error: err instanceof Error ? err.message : "Something went wrong.",
                    });
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);

    return state;
}
